<template>
  <div class="followup-page">

    <!-- Verification -->
    <div v-if="!verified" class="verify-wrapper">
      <div class="verify-card">
        <div class="verify-icon">
          <svg width="28" height="28" fill="none" stroke="#fff" viewBox="0 0 24 24" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"/>
          </svg>
        </div>
        <h2 class="verify-title">{{ $t('followUp.trackTitle') }}</h2>
        <p class="verify-subtitle">{{ $t('followUp.trackSubtitle') }}</p>

        <div class="verify-form">
          <label class="input-label">{{ $t('followUp.referenceId') }} <span class="required">*</span></label>
          <div class="input-wrapper">
            <svg class="input-icon" width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"/>
            </svg>
            <input type="text" v-model="verification.referenceId" @input="handleReferenceIdInput"
              :class="['verify-input', verificationErrors.referenceId ? 'input-error' : '']"
              placeholder="e.g. 3OQDM20251030GU" maxlength="30" autocomplete="off" />
          </div>
          <p v-if="verificationErrors.referenceId" class="field-error">{{ verificationErrors.referenceId }}</p>
          <div v-if="verificationErrors.general" class="general-error">
            <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
            {{ verificationErrors.general }}
          </div>
          <button v-if="!otpRequested" @click="requestFollowUpOtp" :disabled="!verification.referenceId || verificationErrors.referenceId || loading" class="verify-btn">
            <svg v-if="loading" class="spinner" width="16" height="16" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
            </svg>
            {{ loading ? $t('followUp.verifying') : $t('followUp.accessDashboard') }}
          </button>

          <!-- OTP step: shown after a code has been requested -->
          <div v-if="otpRequested">
            <p class="otp-sent-note" v-if="otpChannel === 'whatsapp'">
              {{ $t('followUp.otpSentWhatsappPrefix') }} <strong>{{ otpDestination }}</strong>{{ $t('followUp.otpSentSuffix') }}
            </p>
            <p class="otp-sent-note" v-else>
              {{ $t('followUp.otpSentEmailPrefix') }} <strong>{{ otpDestination }}</strong>{{ $t('followUp.otpSentSuffix') }}
            </p>
            <label class="input-label">{{ $t('followUp.otpLabel') }} <span class="required">*</span></label>
            <div class="input-wrapper">
              <input type="text" v-model="otp" inputmode="numeric" maxlength="6"
                class="verify-input" :placeholder="$t('followUp.otpPlaceholder')" autocomplete="one-time-code" />
            </div>
            <button @click="verifyFollowUpOtp" :disabled="!otp || loading" class="verify-btn">
              <svg v-if="loading" class="spinner" width="16" height="16" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
              </svg>
              {{ loading ? $t('followUp.verifying') : $t('followUp.verifyAndAccess') }}
            </button>
            <button @click="resetOtpStep" class="verify-btn-secondary" type="button">{{ $t('followUp.useDifferentId') }}</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Dashboard -->
    <div v-else class="dashboard">

      <!-- Progress Card -->
      <div class="progress-card" style="margin-top: 50px;">
        <div class="progress-bar-row">
          <div v-for="(step, i) in localizedSteps" :key="i" class="step-col"
               :class="{ 'step-done': isStepDone(i), 'step-active': isStepActive(i) }">
            <div class="step-top">
              <div class="step-dot">
                <svg v-if="isStepDone(i)" width="10" height="10" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
                </svg>
                <span v-else-if="isStepActive(i)" class="active-ring"></span>
              </div>
              <span class="step-label">{{ step.label }}</span>
              <span class="step-percent">{{ step.threshold }}%</span>
            </div>
            <div class="step-track">
              <div class="step-fill" v-if="isStepDone(i) || isStepActive(i)" :style="{ width: getStepFillWidth(i) }"></div>
            </div>
          </div>
        </div>
        <div class="progress-info-row">
          <div class="ref-badge">{{ verification.referenceId }}</div>
          <div class="status-pill" :class="progressPercentage >= 100 ? 'status-closed' : 'status-active'">
            <span class="status-dot"></span>
            {{ progressPercentage >= 100 ? $t('followUp.resolved') : $t('followUp.active') }}
          </div>
          <span class="top-date">{{ $t('followUp.submitted') }} {{ formatDate(incidentDetails.reportedDate) }}</span>
          <button @click="fetchStatus" class="refresh-btn" title="Refresh">
            <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
            </svg>
          </button>
        </div>
      </div>

      <!-- Main Content -->
      <div class="content-grid">
        <div class="col-primary">

          <!-- Incident Details -->
          <section class="card">
            <div class="card-header">
              <h3 class="card-title">{{ $t('followUp.incidentDetails') }}</h3>
              <span class="age-badge">{{ calculateCaseAge() }} {{ $t('followUp.old') }}</span>
            </div>
            <div class="detail-grid">
              <div class="detail-item">
                <span class="detail-label">{{ $t('followUp.type') }}</span>
                <div class="type-tags">
                  <span v-for="(t, i) in parsedIncidentTypes" :key="i" class="type-tag">{{ t }}</span>
                </div>
              </div>
              <div class="detail-item">
                <span class="detail-label">{{ $t('followUp.incidentDate') }}</span>
                <span class="detail-value">{{ formatDateFull(incidentDetails.incidentDate) }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">{{ $t('followUp.assignedTo') }}</span>
                <span class="detail-value">{{ incidentDetails.assignedOfficer || 'CIRT Team' }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">{{ $t('followUp.category') }}</span>
                <span class="detail-value">{{ getIncidentCategory() }}</span>
              </div>
            </div>
            <div class="description-block">
              <span class="detail-label">{{ $t('followUp.description') }}</span>
              <div class="description-text" v-html="decodedDescription"></div>
            </div>
          </section>

          <!-- Evidence -->
          <section class="card">
            <div class="card-header">
              <h3 class="card-title">{{ $t('followUp.evidence') }} <span class="count-badge">{{ incidentDetails.evidence?.length || 0 }}</span></h3>
              <button @click="showUploadArea = !showUploadArea" class="add-btn">
                <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4"/>
                </svg>
                {{ $t('followUp.addFile') }}
              </button>
            </div>

            <div v-if="showUploadArea" class="upload-zone">
              <input ref="evidenceInput" type="file" multiple accept="image/jpeg,image/png,image/gif,application/pdf,.doc,.docx,.txt" @change="handleNewEvidence" class="hidden-input" />
              <div v-if="newFiles.length === 0" class="upload-empty" @click="$refs.evidenceInput.click()">
                <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.5">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"/>
                </svg>
                <span>{{ $t('followUp.selectFiles') }}</span>
                <span class="upload-hint">{{ $t('followUp.fileHint') }}</span>
              </div>
              <div v-else class="upload-files">
                <div v-for="(file, i) in newFiles" :key="i" class="upload-file-row">
                  <div class="upload-file-info">
                    <div class="file-ext-badge" :class="getExtClass(file.name)">{{ getFileExt(file.name) }}</div>
                    <span class="upload-file-name">{{ file.name }}</span>
                    <span class="upload-file-size">{{ formatFileSize(file.size) }}</span>
                  </div>
                  <button @click="removeNewFile(i)" class="remove-file-btn">&times;</button>
                </div>
                <div class="upload-actions">
                  <button @click="$refs.evidenceInput.click()" class="btn-outline-sm">{{ $t('followUp.addMore') }}</button>
                  <button @click="uploadNewEvidence" :disabled="uploading" class="btn-primary-sm">
                    <svg v-if="uploading" class="spinner" width="12" height="12" fill="none" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                    </svg>
                    {{ uploading ? $t('followUp.uploading') : $t('followUp.upload') }}
                  </button>
                  <button @click="cancelUpload" class="btn-ghost-sm">{{ $t('followUp.cancel') }}</button>
                </div>
              </div>
              <p v-if="uploadError" class="upload-msg upload-msg-error">{{ uploadError }}</p>
              <p v-if="uploadSuccess" class="upload-msg upload-msg-success">{{ uploadSuccess }}</p>
            </div>

            <div v-if="incidentDetails.evidence && incidentDetails.evidence.length > 0" class="evidence-grid">
              <div v-for="(item, index) in incidentDetails.evidence" :key="'ev-' + index" class="evidence-card">
                <div class="evidence-preview" @click="openPreview(item, index)">
                  <img v-if="isImage(item.name)" :src="getEvidenceUrl(item)" :alt="'Evidence ' + (index+1)" class="evidence-img" @error="handleImageError($event)" />
                  <div v-else-if="isPdf(item.name)" class="evidence-pdf-icon">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z"/><path d="M14 2v6h6"/></svg>
                    <span>PDF</span>
                  </div>
                  <div v-else class="evidence-generic-icon">
                    <div class="generic-ext" :class="getExtClass(item.name)">{{ getFileExt(item.name) }}</div>
                  </div>
                  <div v-if="isImage(item.name) || isPdf(item.name)" class="preview-overlay">
                    <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path stroke-linecap="round" stroke-linejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
                  </div>
                </div>
                <div class="evidence-info">
                  <span class="evidence-name">Evidence_{{ index + 1 }}</span>
                  <button @click="downloadFile(item, index, 'Evidence')" class="download-btn" :title="$t('followUp.download')">
                    <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/></svg>
                  </button>
                </div>
              </div>
            </div>
            <div v-else class="evidence-empty">
              <svg width="36" height="36" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.2"><path stroke-linecap="round" stroke-linejoin="round" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"/></svg>
              <p>{{ $t('followUp.noEvidence') }}</p>
              <span>{{ $t('followUp.noEvidenceHint') }}</span>
            </div>
          </section>
        </div>

        <!-- Sidebar -->
        <div class="col-sidebar">

          <!-- Timeline -->
          <section class="card">
            <div class="card-header">
              <h3 class="card-title">{{ $t('followUp.timeline') }}</h3>
              <span class="progress-badge">{{ progressPercentage }}%</span>
            </div>
            <div class="timeline">
              <div v-for="(step, i) in localizedSteps" :key="i" class="tl-item" :class="{ 'tl-done': isStepDone(i), 'tl-active': isStepActive(i) }">
                <div class="tl-line-area">
                  <div class="tl-dot">
                    <svg v-if="isStepDone(i)" width="10" height="10" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/></svg>
                  </div>
                  <div v-if="i < steps.length - 1" class="tl-connector" :class="{ 'tl-connector-done': isStepDone(i) }"></div>
                </div>
                <div class="tl-content">
                  <div class="tl-header">
                    <span class="tl-label">{{ step.label }}</span>
                    <span class="tl-right">
                      <span class="tl-percent">{{ step.threshold }}%</span>
                      <span v-if="isStepDone(i)" class="tl-status tl-status-done">{{ $t('followUp.completed') }}</span>
                      <span v-else-if="isStepActive(i)" class="tl-status tl-status-active">{{ $t('followUp.inProgress') }}</span>
                    </span>
                  </div>
                  <p class="tl-desc">{{ step.description }}</p>
                </div>
              </div>
            </div>
          </section>

          <!-- Law Enforcement -->
          <section class="card card-compact">
            <div class="card-header">
              <div class="law-title-row">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2563eb" stroke-width="1.5" class="law-icon">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                  <path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4"/>
                </svg>
                <h3 class="card-title card-title-sm">{{ $t('followUp.lawEnforcement') }}</h3>
              </div>
            </div>
            <div v-if="lawEnforcement.imageUrl || lawEnforcement.reference" class="law-enforcement-content">
              <div v-if="lawEnforcement.imageUrl && !lawEnforcement.imageFailed" class="law-img-wrapper" @click="openLawPreview">
                <img :src="getLawEnforcementProxiedUrl" alt="Law Enforcement Letter" class="law-img" @error="handleLawImageError" />
                <div class="law-img-overlay">
                  <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path stroke-linecap="round" stroke-linejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
                </div>
              </div>
              <div v-else-if="lawEnforcement.imageUrl && lawEnforcement.imageFailed" class="law-img-fallback">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" stroke-width="1.5">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                </svg>
                <span>{{ $t('followUp.previewUnavailable') }}</span>
              </div>
              <div class="law-details">
                <p class="law-desc">{{ $t('followUp.lawDesc') }}</p>
                <div v-if="lawEnforcement.reference" class="law-ref-box">
                  <span class="law-ref-label">{{ $t('followUp.docReference') }}</span>
                  <span class="law-ref-value">{{ lawEnforcement.reference }}</span>
                </div>
                <button v-if="lawEnforcement.imageUrl" @click="downloadLawDoc" class="law-download-btn">
                  <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/></svg>
                  {{ $t('followUp.downloadDocument') }}
                </button>
              </div>
            </div>
            <div v-else class="law-enforcement-placeholder">
              <p>{{ $t('followUp.investigationOngoing') }}</p>
              <span>{{ $t('followUp.docNotAvailable') }}</span>
            </div>
          </section>

          <!-- Plaintiff Form -->
          <section class="card card-compact">
            <div class="card-header">
              <div class="law-title-row">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2563eb" stroke-width="1.5" class="law-icon">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                </svg>
                <h3 class="card-title card-title-sm">{{ $t('followUp.plaintiffForm') }}</h3>
              </div>
            </div>
            <div v-if="plaintiffForm.url" class="law-enforcement-content">
              <p class="law-desc">{{ $t('followUp.plaintiffDesc') }}</p>
              <div class="plaintiff-file-row">
                <div class="file-ext-badge ext-pdf">PDF</div>
                <span class="plaintiff-filename">{{ plaintiffForm.filename }}</span>
              </div>
              <button @click="downloadPlaintiffForm" class="law-download-btn">
                <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/></svg>
                {{ $t('followUp.downloadForm') }}
              </button>
            </div>
            <div v-else class="law-enforcement-placeholder">
              <p>{{ $t('followUp.formNotAvailable') }}</p>
            </div>
          </section>

          <!-- Contact -->
          <section class="card card-contact">
            <h3 class="card-title" style="margin-bottom: 8px;">{{ $t('followUp.needHelp') }}</h3>
            <p class="contact-sub">{{ $t('followUp.includeRef') }}</p>
            <a href="mailto:alerts@cirt.antic.cm" class="contact-row">
              <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>
              <span>alerts@cirt.antic.cm</span>
            </a>
            <div class="contact-row">
              <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/></svg>
              <span>{{ $t('followUp.hotline') }}: <strong>8202</strong></span>
            </div>
            <a href="https://wa.me/237620050515" target="_blank" class="contact-row contact-whatsapp">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="#25D366"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              <span>{{ $t('followUp.whatsapp') }}: <strong>+237 620 050 515</strong></span>
            </a>
          </section>
        </div>
      </div>
    </div>

    <!-- Preview Modal -->
    <Teleport to="body">
      <div v-if="previewItem" class="modal-backdrop" @click.self="closePreview">
        <div class="modal-content">
          <div class="modal-header">
            <span class="modal-title">{{ previewTitle }}</span>
            <div class="modal-actions">
              <button @click="downloadPreviewFile" class="modal-download-btn">
                <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/></svg>
                {{ $t('followUp.download') }}
              </button>
              <button @click="closePreview" class="modal-close-btn">&times;</button>
            </div>
          </div>
          <div class="modal-body">
            <img v-if="isImage(previewItem.name)" :src="getEvidenceUrl(previewItem)" :alt="previewTitle" class="modal-img" />
            <iframe v-else-if="isPdf(previewItem.name)" :src="getEvidenceUrl(previewItem)" class="modal-pdf"></iframe>
          </div>
        </div>
      </div>
    </Teleport>

    <footer class="page-footer">
      <span>{{ $t('followUp.copyright') }}</span>
      <div class="footer-links">
        <a href="#">{{ $t('followUp.privacy') }}</a>
        <a href="#">{{ $t('followUp.terms') }}</a>
        <a href="#">{{ $t('followUp.help') }}</a>
      </div>
    </footer>
  </div>
</template>

<script>
import { getAPI } from '@/axios-api';

export default {
  name: 'IncidentFollowup',

  data() {
    return {
      verified: false,
      loading: false,
      otpRequested: false,
      otp: '',
      otpDestination: '',
      otpChannel: '',
      followUpToken: '',
      verification: { referenceId: '' },
      verificationErrors: { referenceId: null, general: null },
      currentStatus: { label: 'Loading...', value: 0 },
      statusPolling: null,
      incidentDetails: {
        type: '', reportedDate: null, incidentDate: null,
        description: '', assignedOfficer: '', evidence: []
      },
      steps: [
        { labelKey: 'received', descKey: 'receivedDesc', threshold: 25 },
        { labelKey: 'underReview', descKey: 'underReviewDesc', threshold: 50 },
        { labelKey: 'formalization', descKey: 'formalizationDesc', threshold: 75 },
        { labelKey: 'closed', descKey: 'closedDesc', threshold: 100 }
      ],
      lastRefreshTime: new Date(),
      showUploadArea: false,
      newFiles: [],
      uploading: false,
      uploadError: '',
      uploadSuccess: '',
      previewItem: null,
      previewIndex: 0,
      previewTitle: '',
      lawEnforcement: { imageUrl: null, imageName: null, reference: null, imageFailed: false },
      plaintiffForm: { url: null, filename: null, contentType: null }
    };
  },

  computed: {
    progressPercentage() { return this.currentStatus.value; },

    currentStep() {
      let step = this.localizedSteps[0];
      for (const s of this.localizedSteps) {
        if (this.progressPercentage >= s.threshold) step = s;
      }
      return step;
    },

    localizedSteps() {
      return this.steps.map(s => ({
        label: this.$t('followUp.' + s.labelKey),
        description: this.$t('followUp.' + s.descKey),
        threshold: s.threshold
      }));
    },

    parsedIncidentTypes() {
      const raw = this.incidentDetails.type;
      if (!raw || raw === this.$t('followUp.notSpecified')) return [this.$t('followUp.notSpecified')];
      let cleaned = raw.replace(/^\[|\]$/g, '').replace(/['"]/g, '');
      return cleaned.split(',').map(s => {
        let item = s.trim();
        return item.replace(/^[A-Z]{2,4}[\d_]*\s*[-–]\s*/i, '').trim() || item;
      }).filter(Boolean);
    },

    decodedDescription() {
      let desc = this.incidentDetails.description || this.$t('followUp.noDescription');
      desc = this.decodeHtmlEntities(desc);
      desc = desc.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      return desc.replace(/\n/g, '<br>');
    },

    getLawEnforcementProxiedUrl() {
      const url = this.lawEnforcement.imageUrl;
      if (!url) return '';
      // RTIR URLs → proxy through Django backend
      if (this.isRtirUrl(url)) return this.getProxiedUrl(url);
      // Absolute URLs (e.g. http://192.168.x.x/media/...) → strip to path only
      if (url.startsWith('http')) {
        try { return new URL(url).pathname; } catch (_) { return url; }
      }
      // Relative paths → keep as-is
      return url;
    }
  },

  mounted() { this.lastRefreshTime = new Date(); },
  beforeUnmount() { if (this.statusPolling) clearInterval(this.statusPolling); },

  methods: {
    decodeHtmlEntities(text) {
      if (!text) return text;
      const el = document.createElement('textarea');
      el.innerHTML = text;
      return el.value;
    },

    handleReferenceIdInput(event) {
      let value = event.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
      this.verification.referenceId = value;
      this.verificationErrors.referenceId = value.length > 0
        ? (this.validateReferenceIdFormat(value) ? null : this.$t('followUp.invalidFormat'))
        : null;
      this.verificationErrors.general = null;
    },

    validateReferenceIdFormat(id) {
      const checkId = id || this.verification.referenceId;
      if (!checkId) return false;
      return /^\d{1,3}[A-Z]{4}\d{8}[A-Z]{2}$/i.test(checkId) && checkId.length >= 15 && checkId.length <= 25;
    },

    async requestFollowUpOtp() {
      this.verificationErrors = {};
      if (!this.validateReferenceIdFormat()) {
        this.verificationErrors.referenceId = this.$t('followUp.invalidFormatLong');
        return;
      }
      this.loading = true;
      try {
        const res = await getAPI.post('/api/followup/request-otp/', { reference_id: this.verification.referenceId });
        this.otpDestination = res.data.destination || this.$t('followUp.contactOnRecord');
        this.otpChannel = res.data.channel || '';
        this.otpRequested = true;
      } catch (error) {
        if (error.response?.status === 429) {
          this.verificationErrors.general = error.response.data?.error || this.$t('followUp.tooManyRequests');
        } else {
          this.verificationErrors.general = this.$t('followUp.networkError');
        }
      } finally {
        this.loading = false;
      }
    },

    async verifyFollowUpOtp() {
      this.verificationErrors = {};
      if (!this.otp) return;
      this.loading = true;
      try {
        const res = await getAPI.post('/api/followup/verify-otp/', {
          reference_id: this.verification.referenceId,
          otp: this.otp
        });
        if (res.data.session_token) {
          this.followUpToken = res.data.session_token;
          // Attach as default header so track/status calls are authorized.
          getAPI.defaults.headers.common['X-Session-Token'] = res.data.session_token;
          await this.verifyUser();
        } else {
          this.verificationErrors.general = this.$t('followUp.unableVerify');
        }
      } catch (error) {
        if (error.response?.status === 429) {
          this.verificationErrors.general = error.response.data?.error || this.$t('followUp.tooManyAttempts');
        } else {
          this.verificationErrors.general = this.$t('followUp.invalidCode');
        }
      } finally {
        this.loading = false;
      }
    },

    resetOtpStep() {
      this.otpRequested = false;
      this.otp = '';
      this.otpDestination = '';
      this.otpChannel = '';
      this.followUpToken = '';
      delete getAPI.defaults.headers.common['X-Session-Token'];
      this.verificationErrors = {};
    },

    async verifyUser() {
      this.verificationErrors = {};
      if (!this.validateReferenceIdFormat()) {
        this.verificationErrors.referenceId = this.$t('followUp.invalidFormatLong');
        return;
      }
      this.loading = true;
      try {
        const [trackRes, statusRes] = await Promise.all([
          getAPI.get('/api/track/incident/', { params: { reference_id: this.verification.referenceId } }),
          getAPI.get('/api/ticket-status/', { params: { reference_id: this.verification.referenceId } })
        ]);
        if (trackRes.status === 200 && statusRes.status === 200) {
          this.currentStatus.value = statusRes.data.status_value || 0;
          this.currentStatus.label = statusRes.data.status_label || 'Loading...';
          this.incidentDetails = {
            type: trackRes.data.incident_type || this.$t('followUp.notSpecified'),
            reportedDate: trackRes.data.created_at,
            incidentDate: trackRes.data.incident_date,
            description: trackRes.data.incident_description || this.$t('followUp.noDescription'),
            assignedOfficer: 'CIRT',
            evidence: trackRes.data.evidences?.map(e => ({
              name: e.file ? e.file.split('/').pop() : 'Unknown file',
              size: e.file_size || 0,
              uploadDate: e.uploaded_at,
              id: e.id,
              url: e.file
            })) || []
          };
          this.extractInvestigationData(trackRes.data);
          this.verified = true;
          this.startStatusPolling();
        } else {
          this.verificationErrors.general = this.$t('followUp.unableVerify');
        }
      } catch (error) {
        if (error.response?.status === 404) {
          this.verificationErrors.general = this.$t('followUp.notFound');
        } else if (error.response?.status === 400) {
          this.verificationErrors.general = error.response.data.message || this.$t('followUp.invalidRequest');
        } else {
          this.verificationErrors.general = this.$t('followUp.networkError');
        }
      } finally {
        this.loading = false;
      }
    },

    extractInvestigationData(data) {
      this.lawEnforcement = { imageUrl: null, imageName: null, reference: null, imageFailed: false };
      this.plaintiffForm = { url: null, filename: null, contentType: null };

      const invData = data.investigation_data;
      if (!invData) return;

      const reports = invData.incident_reports || [];
      for (const report of reports) {
        if (report.evidence && report.evidence.length > 0) {
          const ev = report.evidence[0];
          const url = ev.download_url || ev.url || null;
          if (url) {
            this.lawEnforcement.imageUrl = url;
            this.lawEnforcement.imageName = ev.filename || url.split('/').pop();
          }
        }
        if (report.descriptions && report.descriptions.length > 0) {
          this.lawEnforcement.reference = this.decodeHtmlEntities(report.descriptions[0]);
        }
        if (this.lawEnforcement.imageUrl) break;
      }

      const forms = invData.plaintiff_forms || [];
      if (forms.length > 0) {
        const form = forms[0];
        this.plaintiffForm = {
          url: form.url || null,
          filename: this.decodeHtmlEntities(form.filename || 'complaint_form.pdf'),
          contentType: form.content_type || 'application/pdf'
        };
      }
    },

    startStatusPolling() {
      if (this.statusPolling) clearInterval(this.statusPolling);
      this.statusPolling = setInterval(() => this.fetchStatus(), 60000);
    },

    async fetchStatus() {
      try {
        const res = await getAPI.get('/api/ticket-status/', { params: { reference_id: this.verification.referenceId } });
        if (res.status === 200 && res.data) {
          this.currentStatus.value = res.data.status_value || 0;
          this.currentStatus.label = res.data.status_label || 'Loading...';
          this.lastRefreshTime = new Date();
        }
      } catch (_) {
        // silent polling failure
      }
    },

    isStepDone(index) {
      const next = index < this.steps.length - 1 ? this.steps[index + 1].threshold : Infinity;
      return this.progressPercentage >= next;
    },

    isStepActive(index) {
      const curr = this.steps[index].threshold;
      const next = index < this.steps.length - 1 ? this.steps[index + 1].threshold : Infinity;
      return this.progressPercentage >= curr && this.progressPercentage < next;
    },

    getStepFillWidth(index) {
      if (this.isStepDone(index)) return '100%';
      if (!this.isStepActive(index)) return '0%';
      const curr = this.steps[index].threshold;
      const next = index < this.steps.length - 1 ? this.steps[index + 1].threshold : 100;
      const range = next - curr;
      if (range <= 0) return '100%';
      const pct = ((this.progressPercentage - curr) / range) * 100;
      return Math.min(100, Math.max(0, pct)) + '%';
    },

    isImage(name) { return name && /\.(jpg|jpeg|png|gif|webp)$/i.test(name); },
    isPdf(name) { return name && /\.pdf$/i.test(name); },
    getFileExt(name) { if (!name) return '?'; const p = name.split('.'); return p.length > 1 ? p.pop().toUpperCase() : '?'; },

    getExtClass(name) {
      if (this.isImage(name)) return 'ext-img';
      if (this.isPdf(name)) return 'ext-pdf';
      if (/\.(doc|docx)$/i.test(name)) return 'ext-doc';
      return 'ext-other';
    },

    /**
     * Normalize any URL to a relative path that works in production.
     *
     * Handles 3 cases:
     *   1. RTIR URLs (contain /REST/2.0/ or rtir.cirt.cm) → proxy via /api/rtir-download/
     *   2. Absolute URLs with internal IP (http://192.168.x.x:8009/media/...) → strip to /media/...
     *   3. Relative paths (/media/...) → keep as-is
     *
     * The browser always resolves the result against the current domain (incident.cirt.cm).
     */
    toRelativeUrl(url) {
      if (!url) return '#';
      // RTIR → proxy
      if (this.isRtirUrl(url)) return this.getProxiedUrl(url);
      // Absolute → extract pathname
      if (url.startsWith('http')) {
        try { return new URL(url).pathname; } catch (_) { return url; }
      }
      // Already relative
      return url;
    },

    /**
     * Get evidence URL for display (img src, iframe src).
     */
    getEvidenceUrl(item) {
        console.log('[EVIDENCE] Raw URL from API:', item.url);
        const result = this.toRelativeUrl(item.url);
        console.log('[EVIDENCE] Resolved URL:', result);
        return result;
    },

    handleImageError(event) {
      event.target.style.display = 'none';
      const p = event.target.parentElement;
      if (p) { const f = document.createElement('div'); f.className = 'evidence-generic-icon'; f.innerHTML = '<span style="color:#9ca3af;font-size:11px;">' + this.$t('followUp.previewUnavailable') + '</span>'; p.appendChild(f); }
    },

    handleLawImageError() { this.lawEnforcement.imageFailed = true; },

    openPreview(item, index) {
      if (this.isImage(item.name) || this.isPdf(item.name)) {
        this.previewItem = item;
        this.previewIndex = index;
        this.previewTitle = 'Evidence_' + (index + 1);
      }
    },

    openLawPreview() {
      if (!this.lawEnforcement.imageUrl || this.lawEnforcement.imageFailed) return;
      const name = this.lawEnforcement.imageName || 'law_enforcement_letter.png';
      this.previewItem = { name: name, url: this.lawEnforcement.imageUrl, isRtir: true };
      this.previewIndex = -1;
      this.previewTitle = this.$t('followUp.lawEnforcement');
    },

    closePreview() { this.previewItem = null; },

    downloadPreviewFile() {
      if (this.previewIndex >= 0) this.downloadFile(this.previewItem, this.previewIndex, 'Evidence');
      else this.downloadLawDoc();
    },

    /**
     * Check if a URL points to RTIR and needs proxying.
     * RTIR URLs contain /REST/2.0/ and come from rtir.cirt.cm or the internal IP.
     */
    isRtirUrl(url) {
      if (!url) return false;
      return url.includes('/REST/2.0/') || url.includes('rtir.cirt.cm');
    },

    /**
     * Convert an RTIR URL to a proxied URL through our Django backend.
     * Uses a relative path so it works in any environment (dev, staging, production).
     * e.g. https://rtir.cirt.cm/REST/2.0/download/cf/4677
     *   → /api/rtir-download/?url=/REST/2.0/download/cf/4677
     */
    getProxiedUrl(url) {
      if (!url) return url;
      if (!this.isRtirUrl(url)) return url;
      // Extract just the /REST/... path
      let rtirPath = url;
      const restIdx = url.indexOf('/REST/');
      if (restIdx !== -1) {
        rtirPath = url.substring(restIdx);
      }
      // Relative URL — browser resolves against current domain
      return '/api/rtir-download/?url=' + encodeURIComponent(rtirPath);
    },

    /**
     * Generic blob download — normalizes all URLs to relative paths first.
     * Works for evidence files, law enforcement docs, and plaintiff forms.
     */
    async blobDownload(url, filename) {
      const downloadUrl = this.toRelativeUrl(url);
      try {
        const r = await fetch(downloadUrl);
        if (!r.ok) throw new Error('Download failed');
        const blob = await r.blob();
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(link.href), 1000);
      } catch (e) {
        console.error('[DOWNLOAD] Blob failed:', e);
        // Fallback: use anchor with download attribute (same-origin)
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    },

    async downloadFile(item, index, prefix) {
      const url = item.url;
      if (!url) return;
      const ext = item.name?.split('.').pop() || 'bin';
      await this.blobDownload(url, prefix + '_' + (index + 1) + '.' + ext);
    },

    async downloadLawDoc() {
      const url = this.lawEnforcement.imageUrl;
      if (!url) return;
      const ext = this.lawEnforcement.imageName?.split('.').pop() || 'png';
      await this.blobDownload(url, 'Law_Enforcement_Letter.' + ext);
    },

    async downloadPlaintiffForm() {
      const url = this.plaintiffForm.url;
      if (!url) return;
      await this.blobDownload(url, this.plaintiffForm.filename || 'Complaint_Form.pdf');
    },

    handleNewEvidence(event) {
      const files = Array.from(event.target.files); this.uploadError = '';
      for (const file of files) {
        if (file.size > 10 * 1024 * 1024) { this.uploadError = file.name + ' ' + this.$t('followUp.exceedsLimit'); continue; }
        this.newFiles.push(file);
      }
      event.target.value = '';
    },

    removeNewFile(i) { this.newFiles.splice(i, 1); },

    cancelUpload() { this.newFiles = []; this.showUploadArea = false; this.uploadError = ''; this.uploadSuccess = ''; },

    async uploadNewEvidence() {
      if (this.newFiles.length === 0) return;
      this.uploading = true; this.uploadError = ''; this.uploadSuccess = '';
      try {
        const fd = new FormData();
        fd.append('reference_id', this.verification.referenceId);
        this.newFiles.forEach(f => fd.append('files', f));
        const res = await getAPI.post('/api/track/incident/add-evidence/', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 });
        if (res.status === 200 || res.status === 201) {
          const added = res.data.evidences?.map(e => ({ name: e.file ? e.file.split('/').pop() : 'Unknown', size: e.file_size || 0, uploadDate: e.uploaded_at, id: e.id, url: e.file })) || [];
          this.incidentDetails.evidence = [...this.incidentDetails.evidence, ...added];
          this.uploadSuccess = this.newFiles.length + ' ' + this.$t('followUp.uploadSuccess');
          this.newFiles = [];
          setTimeout(() => { this.uploadSuccess = ''; this.showUploadArea = false; }, 2000);
        }
      } catch (error) {
        this.uploadError = error.response?.data?.message || this.$t('followUp.uploadFailed');
      } finally { this.uploading = false; }
    },

    formatDate(d) {
      if (!d) return ''; try { return new Date(d).toLocaleDateString(this.$i18n.locale === 'fr' ? 'fr-FR' : 'en-US', { year: 'numeric', month: 'short', day: 'numeric' }); } catch (_) { return ''; }
    },

    formatDateFull(d) {
      if (!d) return 'N/A'; try { return new Date(d).toLocaleDateString(this.$i18n.locale === 'fr' ? 'fr-FR' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' }); } catch (_) { return 'N/A'; }
    },

    formatFileSize(bytes) {
      if (!bytes) return ''; const k = 1024; const s = ['B', 'KB', 'MB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + s[i];
    },

    calculateCaseAge() {
      if (!this.incidentDetails.reportedDate) return 'N/A';
      try {
        const days = Math.floor((new Date() - new Date(this.incidentDetails.reportedDate)) / 86400000);
        if (days === 0) return this.$i18n.locale === 'fr' ? "Aujourd'hui" : 'Today';
        if (days === 1) return '1 ' + (this.$i18n.locale === 'fr' ? 'jour' : 'day');
        return days + ' ' + (this.$i18n.locale === 'fr' ? 'jours' : 'days');
      } catch (_) { return 'N/A'; }
    },

    getIncidentCategory() {
      const t = this.incidentDetails.type?.toLowerCase() || '';
      if (t.includes('phishing')) return 'Phishing';
      if (t.includes('malware')) return 'Malware';
      if (t.includes('breach')) return this.$i18n.locale === 'fr' ? 'Fuite de Données' : 'Data Breach';
      if (t.includes('ddos')) return 'DDoS';
      return this.$t('followUp.securityIncident');
    }
  }
};
</script>

<style scoped>
.followup-page {
  min-height: 100vh; background: #f7f8fa; display: flex; flex-direction: column;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  color: #1a1a2e; overflow-x: hidden;
}
.followup-page *, .followup-page *::before, .followup-page *::after { box-sizing: border-box; }

.verify-wrapper { flex: 1; display: flex; align-items: center; justify-content: center; padding: 40px 16px; }
.verify-card { width: 100%; max-width: 400px; background: #fff; border-radius: 16px; padding: 36px 28px; box-shadow: 0 1px 3px rgba(0,0,0,0.06), 0 8px 24px rgba(0,0,0,0.05); text-align: center; }
.verify-icon { width: 56px; height: 56px; margin: 0 auto 16px; background: linear-gradient(135deg, #1e40af, #3b82f6); border-radius: 14px; display: flex; align-items: center; justify-content: center; }
.verify-title { font-size: 20px; font-weight: 700; margin: 0 0 4px; }
.verify-subtitle { font-size: 13px; color: #6b7280; margin: 0 0 24px; }
.verify-form { text-align: left; }
.input-label { display: block; font-size: 12px; font-weight: 600; color: #374151; margin-bottom: 6px; }
.required { color: #ef4444; }
.input-wrapper { position: relative; }
.input-icon { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: #9ca3af; }
.verify-input { width: 100%; padding: 10px 14px 10px 36px; font-family: 'SF Mono','Fira Code',monospace; font-size: 13px; letter-spacing: 1px; text-transform: uppercase; border: 1.5px solid #d1d5db; border-radius: 10px; background: #fafafa; outline: none; transition: all 0.15s; box-sizing: border-box; }
.verify-input:focus { border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59,130,246,0.12); background: #fff; }
.input-error { border-color: #f87171 !important; }
.field-error { font-size: 11px; color: #ef4444; margin: 5px 0 0; }
.general-error { display: flex; align-items: center; gap: 6px; font-size: 12px; color: #dc2626; background: #fef2f2; border: 1px solid #fecaca; border-radius: 8px; padding: 8px 10px; margin-top: 10px; }
.verify-btn { width: 100%; margin-top: 16px; padding: 11px; font-size: 13px; font-weight: 600; color: #fff; background: linear-gradient(135deg, #1e40af, #2563eb); border: none; border-radius: 10px; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 8px; transition: all 0.15s; }
.verify-btn-secondary { width: 100%; margin-top: 10px; padding: 9px; font-size: 12px; font-weight: 500; color: #2563eb; background: transparent; border: none; cursor: pointer; }
.verify-btn-secondary:hover { text-decoration: underline; }
.otp-sent-note { font-size: 13px; color: #475569; margin: 16px 0 12px; line-height: 1.5; }
.verify-btn:hover:not(:disabled) { filter: brightness(1.08); }
.verify-btn:disabled { opacity: 0.5; cursor: not-allowed; }

.dashboard { flex: 1; max-width: 1140px; margin: 0 auto; padding: 20px 16px 40px; width: 100%; box-sizing: border-box; }

.progress-card { background: #fff; border: 1px solid #e5e7eb; border-radius: 12px; padding: 16px 20px; margin-bottom: 16px; }
.progress-bar-row { display: flex; gap: 0; margin-bottom: 14px; }
.step-col { flex: 1; display: flex; flex-direction: column; gap: 6px; }
.step-top { display: flex; align-items: center; gap: 6px; }
.step-dot { width: 22px; height: 22px; border-radius: 50%; border: 2px solid #d1d5db; background: #fff; display: flex; align-items: center; justify-content: center; color: #d1d5db; flex-shrink: 0; transition: all 0.3s; }
.step-done .step-dot { border-color: #2563eb; background: #2563eb; color: #fff; }
.step-active .step-dot { border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,0.15); }
.active-ring { width: 8px; height: 8px; border-radius: 50%; background: #2563eb; animation: pulse-dot 1.5s infinite; }
.step-label { font-size: 12px; font-weight: 500; color: #9ca3af; white-space: nowrap; }
.step-done .step-label { color: #1e40af; font-weight: 600; }
.step-active .step-label { color: #1e40af; font-weight: 600; }
.step-percent { font-size: 10px; font-weight: 600; color: #c0c0c0; margin-left: auto; padding-right: 6px; }
.step-done .step-percent { color: #2563eb; }
.step-active .step-percent { color: #3b82f6; }
.step-track { height: 4px; background: #e5e7eb; border-radius: 4px; overflow: hidden; margin-right: 2px; }
.step-fill { height: 100%; background: linear-gradient(90deg, #2563eb, #3b82f6); border-radius: 4px; transition: width 0.6s ease; }
.step-col:last-child .step-track { margin-right: 0; }
.progress-info-row { display: flex; align-items: center; gap: 10px; padding-top: 12px; border-top: 1px solid #f3f4f6; }
.ref-badge { font-family: 'SF Mono','Fira Code',monospace; font-size: 13px; font-weight: 700; letter-spacing: 0.5px; color: #1e40af; background: #eff6ff; border: 1px solid #bfdbfe; padding: 5px 12px; border-radius: 8px; }
.status-pill { display: inline-flex; align-items: center; gap: 6px; font-size: 11px; font-weight: 600; padding: 4px 10px; border-radius: 20px; text-transform: uppercase; letter-spacing: 0.5px; }
.status-active { color: #166534; background: #dcfce7; }
.status-closed { color: #6b7280; background: #f3f4f6; }
.status-dot { width: 6px; height: 6px; border-radius: 50%; background: currentColor; }
.status-active .status-dot { animation: pulse-dot 2s infinite; }
@keyframes pulse-dot { 0%, 100% { opacity: 1; } 50% { opacity: 0.3; } }
.top-date { font-size: 12px; color: #6b7280; white-space: nowrap; margin-left: auto; }
.refresh-btn { width: 30px; height: 30px; display: flex; align-items: center; justify-content: center; border: 1px solid #e5e7eb; border-radius: 8px; background: #fff; color: #6b7280; cursor: pointer; transition: all 0.15s; flex-shrink: 0; }
.refresh-btn:hover { border-color: #3b82f6; color: #3b82f6; background: #f0f7ff; }

@media (max-width: 600px) {
  .dashboard { padding: 12px 10px 30px; }
  .progress-card { padding: 12px; border-radius: 10px; }
  .progress-bar-row { overflow-x: auto; -webkit-overflow-scrolling: touch; scroll-snap-type: x mandatory; margin-bottom: 10px; padding-bottom: 4px; }
  .progress-bar-row::-webkit-scrollbar { display: none; }
  .step-col { min-width: 120px; scroll-snap-align: start; flex-shrink: 0; }
  .step-dot { width: 18px; height: 18px; }
  .step-label { font-size: 11px; }
  .step-percent { font-size: 9px; }
  .step-track { height: 3px; }
  .progress-info-row { flex-wrap: wrap; gap: 6px; padding-top: 10px; }
  .ref-badge { font-size: 10px; padding: 4px 8px; letter-spacing: 0.3px; }
  .status-pill { font-size: 10px; padding: 3px 8px; }
  .top-date { font-size: 11px; margin-left: 0; order: 4; width: 100%; }
  .refresh-btn { width: 28px; height: 28px; min-width: 28px; }
  .card { padding: 14px; border-radius: 10px; }
  .card-compact { padding: 12px; }
  .card-title { font-size: 14px; }
  .card-header { margin-bottom: 12px; }
  .detail-item { padding: 8px 10px; }
  .detail-label { font-size: 10px; margin-bottom: 2px; }
  .detail-value { font-size: 12px; }
  .type-tag { font-size: 11px; padding: 2px 8px; }
  .description-text { font-size: 12px; padding: 10px 12px; }
  .evidence-grid { grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)); gap: 8px; }
  .evidence-preview { height: 90px; }
  .evidence-name { font-size: 11px; }
  .tl-label { font-size: 12px; }
  .tl-desc { font-size: 10px; }
  .tl-status { font-size: 8px; padding: 1px 5px; }
  .tl-percent { font-size: 9px; }
  .contact-row { padding: 7px 9px; font-size: 11px; }
  .page-footer { flex-direction: column; gap: 6px; text-align: center; padding: 12px 16px; }
  .modal-backdrop { padding: 10px; }
  .modal-content { border-radius: 10px; max-height: 90vh; }
  .modal-header { padding: 10px 14px; }
  .modal-body { padding: 10px; }
  .modal-img { max-height: 60vh; }
  .modal-pdf { height: 60vh; }
}

.content-grid { display: grid; grid-template-columns: 1fr 320px; gap: 16px; }
@media (max-width: 860px) { .content-grid { grid-template-columns: 1fr; } }
.col-primary { display: flex; flex-direction: column; gap: 16px; }
.col-sidebar { display: flex; flex-direction: column; gap: 16px; }

.card { background: #fff; border-radius: 12px; border: 1px solid #e5e7eb; padding: 20px; }
.card-compact { padding: 14px; }
.card-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; }
.card-compact .card-header { margin-bottom: 6px; }
.card-title { font-size: 15px; font-weight: 700; margin: 0; letter-spacing: -0.2px; }
.card-title-sm { font-size: 13px; }
.count-badge { font-size: 11px; font-weight: 600; color: #3b82f6; background: #eff6ff; padding: 1px 7px; border-radius: 10px; margin-left: 6px; }
.age-badge { font-size: 11px; font-weight: 600; color: #6b7280; background: #f3f4f6; padding: 3px 8px; border-radius: 6px; }
.progress-badge { font-size: 12px; font-weight: 700; color: #2563eb; background: #eff6ff; padding: 3px 10px; border-radius: 6px; border: 1px solid #bfdbfe; }

.detail-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-bottom: 16px; }
@media (max-width: 700px) { .detail-grid { grid-template-columns: 1fr 1fr; gap: 8px; } }
@media (max-width: 400px) { .detail-grid { grid-template-columns: 1fr; } }
.detail-item { padding: 10px 12px; background: #f9fafb; border-radius: 8px; border: 1px solid #f3f4f6; }
.detail-label { display: block; font-size: 11px; font-weight: 600; color: #9ca3af; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px; }
.detail-value { font-size: 13px; font-weight: 600; color: #1f2937; }
.type-tags { display: flex; flex-wrap: wrap; gap: 5px; margin-top: 2px; }
.type-tag { font-size: 12px; font-weight: 500; color: #1e40af; background: #eff6ff; border: 1px solid #dbeafe; padding: 3px 10px; border-radius: 6px; }
.description-block { border-top: 1px solid #f3f4f6; padding-top: 14px; }
.description-text { font-size: 13px; line-height: 1.65; color: #374151; background: #f9fafb; border-radius: 8px; padding: 12px 14px; border: 1px solid #f3f4f6; margin-top: 6px; text-align: justify; }

.add-btn { display: inline-flex; align-items: center; gap: 5px; font-size: 12px; font-weight: 600; color: #2563eb; background: #eff6ff; border: 1px solid #bfdbfe; padding: 5px 12px; border-radius: 8px; cursor: pointer; transition: all 0.15s; }
.add-btn:hover { background: #dbeafe; }
.evidence-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(160px, 1fr)); gap: 10px; }
.evidence-card { border-radius: 10px; border: 1px solid #e5e7eb; overflow: hidden; transition: all 0.15s; background: #fff; }
.evidence-card:hover { border-color: #93c5fd; box-shadow: 0 2px 8px rgba(59,130,246,0.08); }
.evidence-preview { height: 110px; background: #f9fafb; display: flex; align-items: center; justify-content: center; cursor: pointer; position: relative; overflow: hidden; }
.evidence-img { width: 100%; height: 100%; object-fit: cover; }
.evidence-pdf-icon { display: flex; flex-direction: column; align-items: center; gap: 2px; color: #dc2626; }
.evidence-pdf-icon span { font-size: 10px; font-weight: 700; letter-spacing: 1px; }
.evidence-generic-icon { display: flex; align-items: center; justify-content: center; }
.generic-ext { padding: 6px 14px; border-radius: 6px; font-size: 11px; font-weight: 700; }
.preview-overlay { position: absolute; inset: 0; background: rgba(0,0,0,0.4); display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 3px; color: #fff; font-size: 11px; font-weight: 600; opacity: 0; transition: opacity 0.15s; }
.evidence-card:hover .preview-overlay { opacity: 1; }
.evidence-info { display: flex; align-items: center; justify-content: space-between; padding: 8px 10px; border-top: 1px solid #f3f4f6; }
.evidence-name { font-size: 12px; font-weight: 600; color: #374151; }
.download-btn { width: 28px; height: 28px; display: flex; align-items: center; justify-content: center; border: 1px solid #e5e7eb; border-radius: 6px; background: #fff; color: #6b7280; cursor: pointer; transition: all 0.15s; }
.download-btn:hover { color: #2563eb; border-color: #93c5fd; background: #eff6ff; }
.evidence-empty { text-align: center; padding: 30px 10px; color: #9ca3af; }
.evidence-empty p { font-size: 13px; margin: 8px 0 2px; color: #6b7280; font-weight: 500; }
.evidence-empty span { font-size: 11px; }

.upload-zone { margin-bottom: 14px; border: 1.5px dashed #93c5fd; border-radius: 10px; padding: 16px; background: #f0f7ff; }
.hidden-input { display: none; }
.upload-empty { display: flex; flex-direction: column; align-items: center; gap: 6px; padding: 14px; cursor: pointer; color: #6b7280; font-size: 13px; font-weight: 500; }
.upload-empty:hover { color: #2563eb; }
.upload-hint { font-size: 11px; color: #9ca3af; font-weight: 400; }
.upload-files { display: flex; flex-direction: column; gap: 6px; }
.upload-file-row { display: flex; align-items: center; justify-content: space-between; background: #fff; border: 1px solid #e5e7eb; border-radius: 8px; padding: 6px 10px; }
.upload-file-info { display: flex; align-items: center; gap: 8px; min-width: 0; }
.file-ext-badge { font-size: 9px; font-weight: 700; padding: 3px 6px; border-radius: 4px; flex-shrink: 0; }
.ext-img { background: #dcfce7; color: #166534; }
.ext-pdf { background: #fee2e2; color: #991b1b; }
.ext-doc { background: #dbeafe; color: #1e40af; }
.ext-other { background: #f3f4f6; color: #6b7280; }
.upload-file-name { font-size: 12px; font-weight: 500; color: #1f2937; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.upload-file-size { font-size: 11px; color: #9ca3af; flex-shrink: 0; }
.remove-file-btn { background: none; border: none; font-size: 16px; color: #9ca3af; cursor: pointer; padding: 0 4px; line-height: 1; }
.remove-file-btn:hover { color: #ef4444; }
.upload-actions { display: flex; align-items: center; gap: 6px; margin-top: 8px; justify-content: flex-end; }
.btn-outline-sm { font-size: 12px; font-weight: 500; color: #374151; background: #fff; border: 1px solid #d1d5db; padding: 5px 12px; border-radius: 6px; cursor: pointer; }
.btn-primary-sm { font-size: 12px; font-weight: 600; color: #fff; background: #2563eb; border: none; padding: 5px 14px; border-radius: 6px; cursor: pointer; display: flex; align-items: center; gap: 5px; }
.btn-primary-sm:disabled { opacity: 0.5; cursor: not-allowed; }
.btn-ghost-sm { font-size: 12px; color: #6b7280; background: none; border: none; padding: 5px 8px; cursor: pointer; }
.upload-msg { font-size: 12px; margin-top: 8px; padding: 6px 10px; border-radius: 6px; }
.upload-msg-error { color: #dc2626; background: #fef2f2; }
.upload-msg-success { color: #166534; background: #f0fdf4; }

.timeline { display: flex; flex-direction: column; }
.tl-item { display: flex; gap: 8px; }
.tl-line-area { display: flex; flex-direction: column; align-items: center; width: 20px; flex-shrink: 0; }
.tl-dot { width: 20px; height: 20px; border-radius: 50%; border: 2px solid #d1d5db; background: #fff; display: flex; align-items: center; justify-content: center; flex-shrink: 0; color: transparent; transition: all 0.3s; }
.tl-done .tl-dot { border-color: #2563eb; background: #2563eb; color: #fff; }
.tl-active .tl-dot { border-color: #2563eb; background: #eff6ff; box-shadow: 0 0 0 3px rgba(37,99,235,0.12); }
.tl-connector { width: 2px; flex: 1; min-height: 10px; background: #e5e7eb; margin: 2px 0; }
.tl-connector-done { background: #2563eb; }
.tl-content { padding-bottom: 10px; flex: 1; min-width: 0; }
.tl-header { display: flex; align-items: center; gap: 4px; line-height: 20px; }
.tl-label { font-size: 13px; font-weight: 600; color: #9ca3af; }
.tl-done .tl-label { color: #1f2937; }
.tl-active .tl-label { color: #1e40af; }
.tl-right { display: flex; align-items: center; gap: 5px; margin-left: auto; flex-shrink: 0; }
.tl-percent { font-size: 10px; font-weight: 600; color: #b0b0b0; }
.tl-done .tl-percent { color: #16a34a; }
.tl-active .tl-percent { color: #2563eb; }
.tl-desc { font-size: 11px; color: #9ca3af; margin: 1px 0 0; line-height: 1.3; }
.tl-done .tl-desc { color: #6b7280; }
.tl-status { font-size: 9px; font-weight: 700; padding: 1px 6px; border-radius: 4px; text-transform: uppercase; letter-spacing: 0.3px; white-space: nowrap; }
.tl-status-done { color: #166534; background: #dcfce7; }
.tl-status-active { color: #1e40af; background: #dbeafe; }

.law-title-row { display: flex; align-items: center; gap: 6px; }
.law-icon { flex-shrink: 0; }
.law-enforcement-content { display: flex; flex-direction: column; gap: 10px; }
.law-img-wrapper { position: relative; border-radius: 8px; overflow: hidden; border: 1px solid #e5e7eb; cursor: pointer; background: #f9fafb; }
.law-img { width: 100%; display: block; max-height: 200px; object-fit: contain; background: #fff; }
.law-img-overlay { position: absolute; inset: 0; background: rgba(0,0,0,0.35); display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 3px; color: #fff; font-size: 11px; font-weight: 600; opacity: 0; transition: opacity 0.15s; }
.law-img-wrapper:hover .law-img-overlay { opacity: 1; }
.law-img-fallback { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 6px; padding: 20px; background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 8px; color: #9ca3af; font-size: 11px; }
.law-details { display: flex; flex-direction: column; gap: 8px; }
.law-desc { font-size: 11px; line-height: 1.5; color: #4b5563; }
.law-ref-box { background: #f0f7ff; border: 1px solid #bfdbfe; border-radius: 8px; padding: 8px 10px; }
.law-ref-label { display: block; font-size: 10px; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 3px; }
.law-ref-value { font-size: 13px; font-weight: 600; color: #1e40af; line-height: 1.5; word-break: break-word; }
.law-download-btn { display: inline-flex; align-items: center; justify-content: center; gap: 6px; width: 100%; font-size: 11px; font-weight: 600; color: #2563eb; background: #eff6ff; border: 1px solid #bfdbfe; padding: 6px 12px; border-radius: 8px; cursor: pointer; transition: all 0.15s; }
.law-download-btn:hover { background: #dbeafe; }
.law-enforcement-placeholder { text-align: center; padding: 10px 8px; color: #9ca3af; }
.law-enforcement-placeholder p { font-size: 12px; margin: 4px 0 2px; color: #6b7280; font-weight: 500; }
.law-enforcement-placeholder span { font-size: 11px; }

.plaintiff-file-row { display: flex; align-items: center; gap: 8px; background: #f9fafb; border: 1px solid #f3f4f6; border-radius: 8px; padding: 8px 10px; }
.plaintiff-filename { font-size: 11px; font-weight: 500; color: #374151; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

.card-contact { background: linear-gradient(135deg, #f0f7ff, #eff6ff); border-color: #bfdbfe; }
.contact-sub { font-size: 11px; color: #6b7280; margin: 0 0 10px; }
.contact-row { display: flex; align-items: center; gap: 8px; font-size: 12px; color: #1f2937; text-decoration: none; background: rgba(255,255,255,0.7); border: 1px solid #bfdbfe; border-radius: 8px; padding: 8px 10px; margin-bottom: 6px; transition: background 0.15s; }
.contact-row:hover { background: rgba(255,255,255,0.95); }
.contact-whatsapp:hover { background: rgba(37,211,102,0.08); border-color: #25D366; }

.modal-backdrop { position: fixed; inset: 0; z-index: 9999; background: rgba(0,0,0,0.6); display: flex; align-items: center; justify-content: center; padding: 20px; backdrop-filter: blur(2px); }
.modal-content { background: #fff; border-radius: 14px; width: 100%; max-width: 900px; max-height: 85vh; display: flex; flex-direction: column; overflow: hidden; box-shadow: 0 20px 60px rgba(0,0,0,0.2); }
.modal-header { display: flex; align-items: center; justify-content: space-between; padding: 14px 18px; border-bottom: 1px solid #e5e7eb; }
.modal-title { font-size: 14px; font-weight: 700; color: #1f2937; }
.modal-actions { display: flex; align-items: center; gap: 8px; }
.modal-download-btn { display: inline-flex; align-items: center; gap: 5px; font-size: 12px; font-weight: 600; color: #2563eb; background: #eff6ff; border: 1px solid #bfdbfe; padding: 5px 12px; border-radius: 6px; cursor: pointer; transition: background 0.15s; }
.modal-download-btn:hover { background: #dbeafe; }
.modal-close-btn { width: 30px; height: 30px; display: flex; align-items: center; justify-content: center; font-size: 20px; color: #6b7280; background: none; border: 1px solid #e5e7eb; border-radius: 8px; cursor: pointer; line-height: 1; }
.modal-close-btn:hover { background: #f3f4f6; }
.modal-body { flex: 1; overflow: auto; display: flex; align-items: center; justify-content: center; background: #f9fafb; padding: 16px; }
.modal-img { max-width: 100%; max-height: 72vh; object-fit: contain; border-radius: 6px; }
.modal-pdf { width: 100%; height: 72vh; border: none; border-radius: 6px; }

.page-footer { display: flex; align-items: center; justify-content: space-between; padding: 14px 20px; font-size: 11px; color: #9ca3af; border-top: 1px solid #e5e7eb; background: #fff; margin-top: auto; }
.footer-links { display: flex; gap: 16px; }
.footer-links a { color: #9ca3af; text-decoration: none; }
.footer-links a:hover { color: #374151; }
.spinner { animation: spin 0.8s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
</style>